import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

type NavItem = {
  name: string;
  href?: string;
  icon: string;
  children?: NavItem[];
};

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [openMenus, setOpenMenus] = React.useState<Record<string, boolean>>({
    TopUp: true,
  });

  const navigation: NavItem[] = [
    { name: 'Dashboard', href: '/', icon: '📊' },
    { name: 'Card Management', href: '/cards', icon: '💳' },
    {
      name: 'Top Up',
      icon: '💰',
      children: [
        { name: 'Card Payment', href: '/topup/card', icon: '💳' },
        { name: 'QR Payment', href: '/topup/qr', icon: '📷' },
      ],
    },
    { name: 'Transactions', href: '/history', icon: '📝' },
  ];

  const isActiveRoute = React.useCallback(
    (path?: string) => {
      if (!path) return false;
      return path === '/'
        ? location.pathname === '/'
        : location.pathname.startsWith(path);
    },
    [location.pathname]
  );

  const handleNavigate = (href: string) => {
    navigate(href);

    // better mobile UX (avoid layout shift bugs)
    if (window.innerWidth < 1024) onClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    if (window.innerWidth < 1024) onClose();
  };

  const toggleMenu = (name: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-80
          bg-gradient-to-b from-gray-900 to-gray-800 text-white
          flex flex-col
          transform transition-transform duration-300 ease-in-out
          lg:static lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* NAVIGATION */}
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {navigation.map(item => {
            if (item.children) {
              const isOpen = openMenus[item.name];
              const parentActive = item.children.some(c =>
                isActiveRoute(c.href)
              );

              return (
                <div key={item.name}>
                  <button
                    onClick={() => toggleMenu(item.name)}
                    className={`
                      w-full flex items-center justify-between px-4 py-3 rounded-xl
                      transition-all
                      ${parentActive ? 'bg-blue-600' : 'hover:bg-gray-700'}
                    `}
                  >
                    <span className="flex items-center gap-3">
                      <span>{item.icon}</span>
                      <span className="text-sm font-medium">{item.name}</span>
                    </span>
                    <span className="text-xs">{isOpen ? '▾' : '▸'}</span>
                  </button>

                  {isOpen && (
                    <div className="ml-6 mt-2 space-y-1">
                      {item.children.map(child => (
                        <button
                          key={child.name}
                          onClick={() => handleNavigate(child.href!)}
                          className={`
                            w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm
                            transition
                            ${
                              isActiveRoute(child.href)
                                ? 'bg-blue-500'
                                : 'hover:bg-gray-700 text-gray-300'
                            }
                          `}
                        >
                          <span>{child.icon}</span>
                          {child.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <button
                key={item.name}
                onClick={() => handleNavigate(item.href!)}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-xl
                  transition
                  ${
                    isActiveRoute(item.href)
                      ? 'bg-blue-600'
                      : 'hover:bg-gray-700 text-gray-300'
                  }
                `}
              >
                <span>{item.icon}</span>
                <span className="text-sm font-medium">{item.name}</span>
              </button>
            );
          })}
        </nav>

        {/* FOOTER */}
        <div className="p-6 border-t border-gray-700 space-y-4">
          <a
            href="mailto:support@vendingc.com"
            className="text-sm text-gray-400 hover:text-white transition"
          >
            Help & Support
          </a>

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl
              text-gray-400 hover:text-white hover:bg-gray-700 transition"
          >
            <span>🚪</span>
            Sign Out
          </button>

       <p className="text-xs text-gray-500 text-center">
          v1.2.0 • © {new Date().getFullYear()} Vending Central
        </p>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;